import { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/features/auth/AuthContext";
import { useToast } from "@/ui/primitives/use-toast";
import { LGoButton, LGoFormField } from "@/ui/branded";
import { forgotPasswordSchema, type ForgotPasswordInput } from "@/domain/user/schema";

interface ForgotPasswordFormProps {
  onSuccess: () => void;
}

export function ForgotPasswordForm({ onSuccess }: ForgotPasswordFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { resetPassword } = useAuth();
  const { toast } = useToast();

  const form = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: "" },
  });

  const onSubmit = async (data: ForgotPasswordInput) => {
    setIsLoading(true);
    const { error } = await resetPassword(data.email);
    setIsLoading(false);

    if (error) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description: "Nepodařilo se odeslat odkaz pro obnovu hesla. Zkuste to znovu.",
      });
      return;
    }

    toast({
      title: "Odkaz odeslán",
      description: "Zkontrolujte svůj email pro odkaz na obnovu hesla.",
    });
    onSuccess();
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <LGoFormField name="email" label="Email" type="email" placeholder="vas@email.cz" />
        <LGoButton type="submit" variant="primary" loading={isLoading} className="w-full">
          Odeslat odkaz pro obnovu
        </LGoButton>
      </form>
    </FormProvider>
  );
}
